import { useEffect, useMemo } from "react"

import { useHistory } from "react-router-dom"
import { useProductsStore } from "../provider/ProductStoreProvider"
import { useScreenSize } from "../../../hooks"

export function usePagination() {
  const { isDesktop } = useScreenSize()
  const { meta } = useProductsStore()
  const { totalCount, limit } = meta

  const maxPages = isDesktop ? 11 : 5
  const totalPages = Math.ceil(totalCount / limit)

  const history = useHistory()

  const currentSearch = history.location.search

  const currentPage = useMemo(() => {
    const params = new URLSearchParams(currentSearch)
    return Number(params.get("page")) || 1
  }, [currentSearch])

  useEffect(() => {
    const params = new URLSearchParams(history.location.search)

    if (params.get("page") === null) {
      params.set("page", String(currentPage))
      params.set("ordering", "-id")

      history.replace({
        pathname: history.location.pathname,
        search: `?${params.toString()}`,
      })
    }
  }, [currentPage, history])

  const handleChangePage = (page: number) => {
    const params = new URLSearchParams(history.location.search)
    params.set("page", String(page))

    history.push({
      pathname: history.location.pathname,
      search: `?${params.toString()}`,
    })
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handleChangePage(currentPage + 1)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handleChangePage(currentPage - 1)
    }
  }

  const shownPages = useMemo(() => {
    let startPage = 1
    let endPage = totalPages

    if (totalPages > maxPages) {
      const maxPagesBeforeCurrentPage = Math.floor(maxPages / 2)
      const maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1

      if (currentPage <= maxPagesBeforeCurrentPage) {
        endPage = maxPages
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        startPage = totalPages - maxPages + 1
        endPage = totalPages
      } else {
        startPage = currentPage - maxPagesBeforeCurrentPage
        endPage = currentPage + maxPagesAfterCurrentPage
      }
    }

    return Array.from(
      { length: endPage + 1 - startPage },
      (_, i) => startPage + i
    )
  }, [currentPage, maxPages, totalPages])

  return {
    totalPages,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    handleChangePage,
    shownPages,
  }
}
